/**
Copyright (c) 2022-2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { containerService } from '../services/api';

interface TerminalMessage {
  type: 'input' | 'output' | 'resize' | 'connected' | 'error';
  data?: string;
  cols?: number;
  rows?: number;
  error?: string;
}

const TerminalWindow: React.FC = () => {
  const [searchParams] = useSearchParams();
  const containerId = searchParams.get('containerId') || '';
  const projectId = searchParams.get('projectId') || '';
  const containerName = searchParams.get('name') || 'Terminal';

  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstanceRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update window title
  useEffect(() => {
    document.title = `Terminal - ${containerName} | Podstack`;
  }, [containerName]);

  // Send resize message to backend
  const sendResize = useCallback((cols: number, rows: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message: TerminalMessage = {
        type: 'resize',
        cols,
        rows,
      };
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Connect to WebSocket terminal
  const connect = useCallback(() => {
    if (!containerId || !projectId) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setIsConnecting(true);
    setError(null);

    const wsUrl = containerService.getTerminalUrl(containerId, projectId);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);

        // Send initial size
        if (terminalInstanceRef.current) {
          sendResize(
            terminalInstanceRef.current.cols,
            terminalInstanceRef.current.rows
          );
        }
      };

      ws.onmessage = (event) => {
        try {
          const message: TerminalMessage = JSON.parse(event.data);

          switch (message.type) {
            case 'connected':
              terminalInstanceRef.current?.write('\r\n\x1b[32mConnected to container\x1b[0m\r\n\r\n');
              break;
            case 'output':
              if (message.data && terminalInstanceRef.current) {
                terminalInstanceRef.current.write(message.data);
              }
              break;
            case 'error':
              setError(message.error || 'Terminal error');
              terminalInstanceRef.current?.write(
                `\r\n\x1b[31mError: ${message.error}\x1b[0m\r\n`
              );
              break;
          }
        } catch {
          // If not JSON, treat as raw output
          if (terminalInstanceRef.current) {
            terminalInstanceRef.current.write(event.data);
          }
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
        setIsConnected(false);
        setIsConnecting(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsConnecting(false);
        terminalInstanceRef.current?.write('\r\n\x1b[33mDisconnected\x1b[0m\r\n');
      };
    } catch (err) {
      setError('Failed to connect to terminal');
      setIsConnecting(false);
    }
  }, [containerId, projectId, sendResize]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Initialize terminal
  useEffect(() => {
    if (!terminalRef.current || terminalInstanceRef.current) return;
    if (!containerId || !projectId) return;

    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 15,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#0d0d0d',
        foreground: '#d4d4d4',
        cursor: '#ffffff',
        cursorAccent: '#0d0d0d',
        selectionBackground: '#264f78',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#ffffff',
      },
      scrollback: 10000,
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(terminalRef.current);
    fitAddon.fit();

    terminalInstanceRef.current = terminal;
    fitAddonRef.current = fitAddon;

    // Handle terminal input
    terminal.onData((data) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const message: TerminalMessage = {
          type: 'input',
          data,
        };
        wsRef.current.send(JSON.stringify(message));
      }
    });

    // Handle terminal resize
    terminal.onResize(({ cols, rows }) => {
      sendResize(cols, rows);
    });

    // Welcome message
    terminal.write(`\x1b[1;32mPodstack Terminal\x1b[0m - ${containerName}\r\n`);
    terminal.write('Connecting to container...\r\n\r\n');

    // Connect to WebSocket
    connect();

    // Handle window resize
    const handleResize = () => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
      }
    };
    window.addEventListener('resize', handleResize);

    // Focus terminal
    terminal.focus();

    return () => {
      window.removeEventListener('resize', handleResize);
      disconnect();
      terminal.dispose();
      terminalInstanceRef.current = null;
      fitAddonRef.current = null;
    };
  }, [containerId, projectId, containerName, connect, disconnect, sendResize]);

  const handleReconnect = () => {
    disconnect();
    terminalInstanceRef.current?.clear();
    terminalInstanceRef.current?.write('Reconnecting...\r\n\r\n');
    setTimeout(connect, 500);
  };

  if (!containerId || !projectId) {
    return (
      <div className="h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg">Invalid terminal session</p>
          <p className="text-gray-400 mt-2">Missing container or project ID</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0d0d0d] flex flex-col">
      {/* Header Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <span className="text-white font-medium">{containerName}</span>
          {isConnected ? (
            <span className="flex items-center gap-1 text-xs text-green-500 bg-green-500/20 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Connected
            </span>
          ) : isConnecting ? (
            <span className="flex items-center gap-1 text-xs text-yellow-500 bg-yellow-500/20 px-2 py-0.5 rounded-full">
              <ArrowPathIcon className="w-3 h-3 animate-spin" />
              Connecting
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-red-500 bg-red-500/20 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
              Disconnected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReconnect}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded"
            title="Reconnect"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Terminal */}
      <div
        ref={terminalRef}
        className="flex-1"
        style={{ backgroundColor: '#0d0d0d' }}
      />

      {/* Error overlay */}
      {error && !isConnected && (
        <div className="absolute bottom-4 left-4 right-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <button
            onClick={handleReconnect}
            className="mt-2 text-xs text-red-400 hover:text-red-300 underline"
          >
            Try reconnecting
          </button>
        </div>
      )}
    </div>
  );
};

export default TerminalWindow;
